import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Modal } from '@/components/ui/Modal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Calendar, BarChart3, Clock, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { civilDateKeyInAppTz, formatDate } from '@/lib/utils';
import { ZehMotocaIndicadoresPanel } from '@/components/zehmotoca/ZehMotocaIndicadoresPanel';

const STAFF_ROLES = [
  { value: 'MECANICO', label: 'Mecânico' },
  { value: 'TECNICO', label: 'Técnico' },
  { value: 'INSPETOR', label: 'Inspetor' },
];

const CLASSIFICACOES = [
  { value: 'EXPRESSO', label: 'Expresso', color: 'text-green-600' },
  { value: 'INTERMEDIARIO', label: 'Intermediário', color: 'text-yellow-600' },
  { value: 'TECNICO', label: 'Técnico', color: 'text-red-600' },
];

const STATUS_LABELS: Record<string, string> = {
  AGENDADO: 'Agendado',
  CHECK_IN: 'Check-in',
  EM_TRIAGEM: 'Em triagem',
  EM_ATENDIMENTO: 'Em atendimento',
  CONCLUIDO: 'Concluído',
  CANCELADO: 'Cancelado',
  NAO_COMPARECEU: 'Não compareceu',
};

const KANBAN_STATUS_ORDER = [
  'AGENDADO',
  'CHECK_IN',
  'EM_TRIAGEM',
  'EM_ATENDIMENTO',
  'CONCLUIDO',
  'NAO_COMPARECEU',
  'CANCELADO',
] as const;

const STATUS_COLUMN_THEME: Record<string, { header: string; dot: string }> = {
  AGENDADO: { header: 'bg-slate-100 border-slate-200', dot: 'bg-slate-500' },
  CHECK_IN: { header: 'bg-sky-50 border-sky-200', dot: 'bg-sky-500' },
  EM_TRIAGEM: { header: 'bg-violet-50 border-violet-200', dot: 'bg-violet-500' },
  EM_ATENDIMENTO: { header: 'bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  CONCLUIDO: { header: 'bg-emerald-50 border-emerald-200', dot: 'bg-emerald-600' },
  NAO_COMPARECEU: { header: 'bg-orange-50 border-orange-200', dot: 'bg-orange-500' },
  CANCELADO: { header: 'bg-rose-50 border-rose-200', dot: 'bg-rose-500' },
};

const CLASSIFICACAO_BADGE: Record<string, { label: string; className: string }> = {
  EXPRESSO: { label: 'Expresso', className: 'bg-green-100 text-green-800 border-green-200' },
  INTERMEDIARIO: {
    label: 'Intermediário',
    className: 'bg-yellow-100 text-yellow-900 border-yellow-200',
  },
  TECNICO: { label: 'Técnico', className: 'bg-red-100 text-red-800 border-red-200' },
};

type AgendamentoItem = {
  id: string;
  status: string;
  data?: string;
  dataFormatada?: string;
  hora?: string;
  classificacao?: string | null;
  avaliacao?: string | null;
  regiaoProblemaLabel?: string;
  audioUrl?: string | null;
  audioTranscricao?: string | null;
  cliente?: { nome?: string; placa?: string | null };
};

function AgendamentoKanbanCard({
  ag,
  onCancelar,
}: {
  ag: AgendamentoItem;
  onCancelar: (id: string) => void;
}) {
  const classif = ag.classificacao ? CLASSIFICACAO_BADGE[ag.classificacao] : null;

  return (
    <Card className="border border-border/80 shadow-sm transition-shadow hover:shadow-md">
      <CardContent className="space-y-2 p-3 text-sm">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-semibold leading-tight truncate" title={ag.cliente?.nome}>
              {ag.cliente?.nome ?? '—'}
            </p>
            <p className="text-xs text-muted-foreground font-mono mt-0.5">{ag.cliente?.placa ?? '—'}</p>
          </div>
          {classif ? (
            <span
              className={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${classif.className}`}
            >
              {classif.label}
            </span>
          ) : (
            <span className="shrink-0 rounded-md border border-dashed border-muted-foreground/40 px-2 py-0.5 text-[10px] text-muted-foreground">
              Sem classif.
            </span>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          {ag.dataFormatada ?? (ag.data ? formatDate(ag.data) : '—')} · {ag.hora ?? '—'}
        </p>

        {ag.regiaoProblemaLabel ? (
          <p className="text-xs leading-snug line-clamp-2 text-foreground/90" title={ag.regiaoProblemaLabel}>
            {ag.regiaoProblemaLabel}
          </p>
        ) : null}

        {ag.audioTranscricao ? (
          <p className="text-xs italic text-muted-foreground line-clamp-2" title={ag.audioTranscricao}>
            {ag.audioTranscricao}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-border/60">
          {ag.avaliacao ? (
            <span className="text-[10px] font-medium text-primary">Aval.: {ag.avaliacao}</span>
          ) : null}
          {ag.audioUrl ? (
            <a
              href={ag.audioUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[10px] text-primary hover:underline"
            >
              Áudio
            </a>
          ) : null}
          {['AGENDADO', 'CHECK_IN'].includes(ag.status) ? (
            <button
              type="button"
              className="text-[10px] text-destructive hover:underline ml-auto"
              onClick={() => onCancelar(ag.id)}
            >
              Cancelar
            </button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

interface Props {
  companies: { id: string; name: string }[];
}

export const ZehMotocaAgendamentoTabs: React.FC<Props> = ({ companies }) => {
  const [subTab, setSubTab] = useState<'janelas' | 'agendamentos' | 'indicadores'>('janelas');
  const [companyId, setCompanyId] = useState('');
  const [janelas, setJanelas] = useState<any[]>([]);
  const [savingJanelas, setSavingJanelas] = useState(false);
  const [direcionamento, setDirecionamento] = useState<any[]>([]);
  const [overrides, setOverrides] = useState<any[]>([]);
  const [overrideForm, setOverrideForm] = useState({ data: '', janelaConfigId: '', vagas: 0 });
  const [agendamentos, setAgendamentos] = useState<any[]>([]);
  const [agendamentosTotal, setAgendamentosTotal] = useState(0);
  const [metricas, setMetricas] = useState<any>(null);
  const [filtroDataFrom, setFiltroDataFrom] = useState(() => civilDateKeyInAppTz());
  const [filtroDataTo, setFiltroDataTo] = useState(() => civilDateKeyInAppTz());
  const [loading, setLoading] = useState(false);
  const [cancelAgendamentoId, setCancelAgendamentoId] = useState<string | null>(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');
  const [cancelando, setCancelando] = useState(false);

  useEffect(() => {
    if (companies.length === 1 && !companyId) setCompanyId(companies[0]!.id);
  }, [companies, companyId]);

  useEffect(() => {
    if (!companyId) return;
    if (subTab === 'janelas') {
      void loadJanelas();
      void loadDirecionamento();
      void loadOverrides();
    } else if (subTab === 'agendamentos') void loadAgendamentos();
    else if (subTab === 'indicadores') void loadMetricas();
  }, [subTab, companyId, filtroDataFrom, filtroDataTo]);

  const loadJanelas = async () => {
    setLoading(true);
    try {
      const res = await api.get('/configuracoes/aplicativo-zehmotoca/oficina-janelas', {
        params: { companyId },
      });
      setJanelas(res.data.data ?? []);
    } catch {
      toast.error('Erro ao carregar janelas');
    } finally {
      setLoading(false);
    }
  };

  const loadDirecionamento = async () => {
    try {
      const res = await api.get('/configuracoes/aplicativo-zehmotoca/oficina-direcionamento', {
        params: { companyId },
      });
      setDirecionamento(res.data.data ?? []);
    } catch {
      toast.error('Erro ao carregar direcionamento');
    }
  };

  const loadOverrides = async () => {
    try {
      const res = await api.get('/configuracoes/aplicativo-zehmotoca/oficina-janelas/overrides', {
        params: { companyId },
      });
      setOverrides(res.data.data ?? []);
    } catch {
      /* ignore */
    }
  };

  const saveJanelas = async () => {
    setSavingJanelas(true);
    try {
      await api.put('/configuracoes/aplicativo-zehmotoca/oficina-janelas', { companyId, janelas });
      toast.success('Horários salvos');
      await loadJanelas();
    } catch {
      toast.error('Erro ao salvar');
    } finally {
      setSavingJanelas(false);
    }
  };

  const saveDirecionamento = async () => {
    try {
      await api.put('/configuracoes/aplicativo-zehmotoca/oficina-direcionamento', {
        companyId,
        regras: direcionamento.map((r) => ({
          classificacao: r.classificacao,
          roleAlvo: r.roleAlvo,
        })),
      });
      toast.success('Direcionamento salvo');
    } catch {
      toast.error('Erro ao salvar direcionamento');
    }
  };

  const addOverride = async () => {
    if (!overrideForm.data || !overrideForm.janelaConfigId) {
      toast.error('Preencha data e janela');
      return;
    }
    try {
      await api.post('/configuracoes/aplicativo-zehmotoca/oficina-janelas/overrides', {
        companyId,
        ...overrideForm,
        vagas: Number(overrideForm.vagas),
      });
      toast.success('Exceção salva');
      setOverrideForm({ data: '', janelaConfigId: '', vagas: 0 });
      await loadOverrides();
    } catch {
      toast.error('Erro ao salvar exceção');
    }
  };

  const deleteOverride = async (id: string) => {
    try {
      await api.delete(`/configuracoes/aplicativo-zehmotoca/oficina-janelas/overrides/${id}`);
      await loadOverrides();
    } catch {
      toast.error('Erro ao remover');
    }
  };

  const loadAgendamentos = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const dataFrom =
        filtroDataFrom && filtroDataTo && filtroDataFrom > filtroDataTo
          ? filtroDataTo
          : filtroDataFrom;
      const dataTo =
        filtroDataFrom && filtroDataTo && filtroDataFrom > filtroDataTo
          ? filtroDataFrom
          : filtroDataTo;
      const res = await api.get('/configuracoes/aplicativo-zehmotoca/oficina-agendamentos', {
        params: {
          companyId,
          dataFrom: dataFrom || undefined,
          dataTo: dataTo || undefined,
          limit: 200,
        },
      });
      const payload = res.data?.data;
      setAgendamentos(payload?.data ?? []);
      setAgendamentosTotal(payload?.total ?? 0);
    } catch {
      toast.error('Erro ao listar agendamentos');
      setAgendamentos([]);
      setAgendamentosTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const loadMetricas = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const dataFrom =
        filtroDataFrom && filtroDataTo && filtroDataFrom > filtroDataTo
          ? filtroDataTo
          : filtroDataFrom;
      const dataTo =
        filtroDataFrom && filtroDataTo && filtroDataFrom > filtroDataTo
          ? filtroDataFrom
          : filtroDataTo;
      const res = await api.get('/configuracoes/aplicativo-zehmotoca/oficina-agendamentos/metricas', {
        params: { companyId, dataFrom: dataFrom || undefined, dataTo: dataTo || undefined },
      });
      setMetricas(res.data.data);
    } catch {
      toast.error('Erro ao carregar indicadores');
      setMetricas(null);
    } finally {
      setLoading(false);
    }
  };

  const agendamentosPorStatus = useMemo(() => {
    const map: Record<string, AgendamentoItem[]> = {};
    for (const s of KANBAN_STATUS_ORDER) map[s] = [];
    for (const a of agendamentos as AgendamentoItem[]) {
      const key =
        a.status in STATUS_LABELS && KANBAN_STATUS_ORDER.some((s) => s === a.status)
          ? a.status
          : 'AGENDADO';
      map[key]!.push(a);
    }
    return map;
  }, [agendamentos]);

  const abrirCancelamento = (id: string) => {
    setCancelAgendamentoId(id);
    setMotivoCancelamento('');
  };

  const fecharCancelamento = () => {
    if (cancelando) return;
    setCancelAgendamentoId(null);
    setMotivoCancelamento('');
  };

  const confirmarCancelamento = async () => {
    if (!cancelAgendamentoId) return;
    const motivo = motivoCancelamento.trim();
    if (motivo.length < 3) {
      toast.error('Informe o motivo do cancelamento (mínimo 3 caracteres)');
      return;
    }
    setCancelando(true);
    try {
      await api.patch(
        `/configuracoes/aplicativo-zehmotoca/oficina-agendamentos/${cancelAgendamentoId}`,
        {
          status: 'CANCELADO',
          motivoCancelamento: motivo,
        }
      );
      toast.success('Agendamento cancelado');
      fecharCancelamento();
      await loadAgendamentos();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao cancelar';
      toast.error(msg);
    } finally {
      setCancelando(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-sm font-medium">Empresa</label>
          <select
            className="mt-1 block w-64 rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
          >
            <option value="">Selecione...</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <Button
            variant={subTab === 'janelas' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSubTab('janelas')}
          >
            <Clock className="w-4 h-4 mr-1" />
            Horários e vagas
          </Button>
          <Button
            variant={subTab === 'agendamentos' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSubTab('agendamentos')}
          >
            <Calendar className="w-4 h-4 mr-1" />
            Agendamentos
          </Button>
          <Button
            variant={subTab === 'indicadores' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSubTab('indicadores')}
          >
            <BarChart3 className="w-4 h-4 mr-1" />
            Indicadores
          </Button>
        </div>
      </div>

      {!companyId ? (
        <p className="text-muted-foreground text-sm">Selecione uma empresa para continuar.</p>
      ) : subTab === 'janelas' ? (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Janelas diárias (padrão)</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-sm text-muted-foreground">Carregando...</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-2">Hora</th>
                      <th>Vagas padrão</th>
                      <th>Ativo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {janelas.map((j, idx) => (
                      <tr key={j.id} className="border-b">
                        <td className="py-2 font-medium">{j.hora}</td>
                        <td>
                          <Input
                            type="number"
                            className="w-24"
                            value={j.vagasPadrao}
                            onChange={(e) => {
                              const next = [...janelas];
                              next[idx] = { ...j, vagasPadrao: Number(e.target.value) };
                              setJanelas(next);
                            }}
                          />
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={j.ativo}
                            onChange={(e) => {
                              const next = [...janelas];
                              next[idx] = { ...j, ativo: e.target.checked };
                              setJanelas(next);
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <Button className="mt-4" onClick={() => void saveJanelas()} disabled={savingJanelas}>
                Salvar janelas
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Direcionamento pós-triagem</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {CLASSIFICACOES.map((c) => {
                const regra = direcionamento.find((r) => r.classificacao === c.value);
                return (
                  <div key={c.value} className="flex items-center gap-4">
                    <span className={`w-32 font-medium ${c.color}`}>{c.label}</span>
                    <select
                      className="rounded-md border px-2 py-1 text-sm"
                      value={regra?.roleAlvo ?? 'MECANICO'}
                      onChange={(e) => {
                        const next = [...direcionamento];
                        const i = next.findIndex((r) => r.classificacao === c.value);
                        if (i >= 0) next[i] = { ...next[i], roleAlvo: e.target.value };
                        else next.push({ classificacao: c.value, roleAlvo: e.target.value });
                        setDirecionamento(next);
                      }}
                    >
                      {STAFF_ROLES.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
              <Button size="sm" onClick={() => void saveDirecionamento()}>
                Salvar direcionamento
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Exceções por data</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2 items-end">
                <div>
                  <label className="block text-sm font-medium">Data</label>
                  <Input
                    type="date"
                    value={overrideForm.data}
                    onChange={(e) => setOverrideForm({ ...overrideForm, data: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">Janela</label>
                  <select
                    className="block rounded-md border px-2 py-2 text-sm"
                    value={overrideForm.janelaConfigId}
                    onChange={(e) =>
                      setOverrideForm({ ...overrideForm, janelaConfigId: e.target.value })
                    }
                  >
                    <option value="">—</option>
                    {janelas.map((j) => (
                      <option key={j.id} value={j.id}>
                        {j.hora}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium">Vagas</label>
                  <Input
                    type="number"
                    className="w-20"
                    value={overrideForm.vagas}
                    onChange={(e) =>
                      setOverrideForm({ ...overrideForm, vagas: Number(e.target.value) })
                    }
                  />
                </div>
                <Button onClick={() => void addOverride()}>Adicionar</Button>
              </div>
              <ul className="text-sm space-y-1">
                {overrides.map((o) => (
                  <li key={o.id} className="flex justify-between items-center border-b py-1">
                    <span>
                      {String(o.data).slice(0, 10)} — {o.janelaConfig?.hora}: {o.vagas} vagas
                    </span>
                    <button type="button" onClick={() => void deleteOverride(o.id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      ) : subTab === 'agendamentos' ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">De</label>
              <Input
                type="date"
                value={filtroDataFrom}
                onChange={(e) => setFiltroDataFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Até</label>
              <Input
                type="date"
                value={filtroDataTo}
                onChange={(e) => setFiltroDataTo(e.target.value)}
              />
            </div>
            <Button variant="outline" onClick={() => void loadAgendamentos()}>
              Filtrar
            </Button>
            
          </div>
          {loading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : agendamentos.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhum agendamento entre {formatDate(filtroDataFrom)} e {formatDate(filtroDataTo)}.
            </p>
          ) : (
            <>
              {agendamentosTotal > agendamentos.length ? (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  Exibindo {agendamentos.length} de {agendamentosTotal} no período (limite 200).
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {agendamentosTotal} agendamento(s) no período.
                </p>
              )}
            <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1">
              {KANBAN_STATUS_ORDER.map((statusKey) => {
                const items = agendamentosPorStatus[statusKey] ?? [];
                const theme = STATUS_COLUMN_THEME[statusKey] ?? STATUS_COLUMN_THEME.AGENDADO;
                return (
                  <div
                    key={statusKey}
                    className="flex w-[min(100%,280px)] shrink-0 flex-col rounded-lg border bg-muted/20"
                  >
                    <div
                      className={`flex items-center justify-between gap-2 rounded-t-lg border-b px-3 py-2.5 ${theme.header}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`h-2 w-2 shrink-0 rounded-full ${theme.dot}`} />
                        <span className="text-sm font-semibold truncate">
                          {STATUS_LABELS[statusKey] ?? statusKey}
                        </span>
                      </div>
                      <span className="shrink-0 rounded-full bg-background/80 px-2 py-0.5 text-xs font-bold tabular-nums">
                        {items.length}
                      </span>
                    </div>
                    <div className="flex flex-col gap-2 p-2 max-h-[min(70vh,640px)] overflow-y-auto">
                      {items.length === 0 ? (
                        <p className="py-6 text-center text-xs text-muted-foreground">Vazio</p>
                      ) : (
                        items.map((a) => (
                          <AgendamentoKanbanCard
                            key={a.id}
                            ag={a}
                            onCancelar={abrirCancelamento}
                          />
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">De</label>
              <Input
                type="date"
                value={filtroDataFrom}
                onChange={(e) => setFiltroDataFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Até</label>
              <Input
                type="date"
                value={filtroDataTo}
                onChange={(e) => setFiltroDataTo(e.target.value)}
              />
            </div>
            <Button variant="outline" onClick={() => void loadMetricas()}>
              Atualizar
            </Button>
          </div>
          {loading && !metricas ? (
            <p className="text-sm text-muted-foreground">Carregando indicadores...</p>
          ) : metricas ? (
            <ZehMotocaIndicadoresPanel
              metricas={metricas}
              dataFrom={filtroDataFrom}
              dataTo={filtroDataTo}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              Selecione a empresa e o período, depois clique em Atualizar.
            </p>
          )}
        </div>
      )}

      <Modal
        isOpen={!!cancelAgendamentoId}
        onClose={fecharCancelamento}
        title="Cancelar agendamento"
        size="sm"
      >
        <p className="text-sm text-muted-foreground mb-4">
          Informe o motivo do cancelamento. O registro ficará disponível para consulta na oficina.
        </p>
        <label className="block text-sm font-medium mb-1">Motivo do cancelamento</label>
        <Textarea
          value={motivoCancelamento}
          onChange={(e) => setMotivoCancelamento(e.target.value)}
          placeholder="Ex.: cliente solicitou reagendamento"
          maxLength={500}
          rows={4}
          disabled={cancelando}
        />
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={fecharCancelamento} disabled={cancelando}>
            Voltar
          </Button>
          <Button
            variant="destructive"
            onClick={() => void confirmarCancelamento()}
            disabled={cancelando || motivoCancelamento.trim().length < 3}
          >
            {cancelando ? 'Cancelando...' : 'Confirmar cancelamento'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};
